import React, {useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import {Button, CardActions, CardMedia, Chip, Tooltip} from '@mui/material';

import TerminalIcon from '@mui/icons-material/Terminal';

import ArticleIcon from '@mui/icons-material/Article';
import CircleIcon from '@mui/icons-material/Circle';
import CloudIcon from '@mui/icons-material/Cloud';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import {softs, tagCategories} from './meta/contrib';
import {authors} from './meta/authors';

const WHITE_PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 9"><rect width="16" height="9" fill="white"/></svg>';

const ALL_YEARS = 'all';
const UNKNOWN_YEAR = 'unknown';

const TAG_COLORS = tagCategories.reduce((colors, category) => {
    category.tags.forEach((tag) => {
        colors[tag] = category.color;
    });
    return colors;
}, {});

const DEFAULT_TAG_COLOR = '#64748b';

function getTagColor(tag) {
    return TAG_COLORS[tag] || DEFAULT_TAG_COLOR;
}

function tagChipSx(tag, selected) {
    const color = getTagColor(tag);
    return selected
        ? {backgroundColor: color, borderColor: color, color: '#fff', '&:hover': {backgroundColor: color, opacity: 0.9}}
        : {borderColor: color, color};
}

function toggleItem(set, item) {
    const next = new Set(set);
    if (next.has(item)) {
        next.delete(item);
    } else {
        next.add(item);
    }
    return next;
}

export default function Soft() {
    const [selectedYear, setSelectedYear] = useState(ALL_YEARS);
    const [selectedTags, setSelectedTags] = useState(new Set());

    const sortedSofts = useMemo(() => [...softs].sort((left, right) => {
        const leftYear = Number(left.year) || 0;
        const rightYear = Number(right.year) || 0;
        if (leftYear !== rightYear) {
            return rightYear - leftYear;
        }
        return (left.name || '').localeCompare(right.name || '');
    }), []);

    const availableYears = useMemo(() => {
        const years = new Set(softs.map((soft) => soft.year).filter(Boolean));
        return Array.from(years).sort((left, right) => Number(right) - Number(left));
    }, []);

    const tagGroups = useMemo(() => {
        const used = new Set();
        softs.forEach((soft) => (soft.tags || []).forEach((tag) => used.add(tag)));
        return tagCategories
            .map((category) => ({
                ...category,
                tags: category.tags.filter((tag) => used.has(tag))
            }))
            .filter((category) => category.tags.length > 0);
    }, []);

    const visibleSofts = useMemo(() => sortedSofts.filter((soft) => {
        const yearOk = selectedYear === ALL_YEARS || soft.year === selectedYear;
        const tagOk = selectedTags.size === 0
            || (soft.tags || []).some((tag) => selectedTags.has(tag));
        return yearOk && tagOk;
    }), [sortedSofts, selectedYear, selectedTags]);

    const hasFilter = selectedYear !== ALL_YEARS || selectedTags.size > 0;

    function getIcon(type) {
        if (type === 'soft') {
            return <Tooltip title={type}><TerminalIcon color="primary"/></Tooltip>;
        }
        if (type === 'platform') {
            return <Tooltip title={type}><CloudIcon/></Tooltip>
        }
        if (type === 'paper') {
            return <Tooltip title={type}><ArticleIcon/></Tooltip>
        }
        if (type === 'demo') {
            return <Tooltip title={type}><CoPresentIcon/></Tooltip>
        }
        if (type === 'book') {
            return <Tooltip title={type}><ImportContactsIcon/></Tooltip>
        }
        return <Tooltip title={type}><CircleIcon/></Tooltip>;
    }

    function openExternal(url) {
        if (!url) {
            return;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    const renderSoftCard = (soft) => {
        const cardKey = `${soft.name || 'soft'}-${soft.year || UNKNOWN_YEAR}`;
        const knownAuthors = (soft.authors || []).map((id) => authors[id]).filter(Boolean);

        return (
            <Card
                key={cardKey}
                variant="outlined"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 3,
                    overflow: 'hidden',
                    borderColor: '#e3e7ee',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 28px rgba(20, 36, 70, 0.12)'
                    }
                }}
            >
                <CardMedia
                    sx={{height: 170, backgroundColor: '#fff'}}
                    image={soft.screenshot || WHITE_PLACEHOLDER_IMAGE}
                    title={soft.name || 'software image'}
                />
                <CardContent sx={{pb: 1}}>
                    <Stack spacing={1.25}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            {getIcon(soft.type)}
                            <Typography variant="h6" sx={{fontSize: '1rem', lineHeight: 1.25}}>
                                {soft.name}
                            </Typography>

                            {soft.year && (
                                <Typography variant="caption" sx={{ml: 'auto', color: 'text.secondary'}}>
                                    {soft.year}
                                </Typography>
                            )}
                        </Box>

                        <Typography variant="body2" sx={{color: 'text.secondary', minHeight: 66}}>
                            {soft.description}
                        </Typography>

                        {(soft.tags || []).length > 0 && (
                            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                {soft.tags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setSelectedTags((previous) => toggleItem(previous, tag))}
                                        sx={{height: 22, fontSize: '0.7rem', ...tagChipSx(tag, selectedTags.has(tag))}}
                                    />
                                ))}
                            </Stack>
                        )}

                        {knownAuthors.length > 0 && (
                            <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                {knownAuthors.map((author, index) => (
                                    <React.Fragment key={author.name}>
                                        {index > 0 ? ', ' : ''}
                                        {author.url ? (
                                            <Link href={author.url} target="_blank" rel="noreferrer" underline="hover">
                                                {author.name}
                                            </Link>
                                        ) : author.name}
                                    </React.Fragment>
                                ))}
                            </Typography>
                        )}
                    </Stack>
                </CardContent>
                <CardActions sx={{display: 'flex', justifyContent: 'space-between', px: 2, pb: 2}}>
                    <Button
                        variant="outlined"
                        disabled={!soft.download}
                        size="small"
                        onClick={() => {
                            openExternal(soft.download);
                        }}
                    >
                        Download
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={!soft.url}
                        size="small"
                        onClick={() => {
                            openExternal(soft.url);
                        }}
                    >
                        URL
                    </Button>

                    <Button
                        variant="outlined"
                        disabled={!soft.paper}
                        size="small"
                        onClick={() => {
                            openExternal(soft.paper);
                        }}
                    >
                        Article
                    </Button>
                </CardActions>
            </Card>
        );
    };

    return (
        <Box sx={{width: '100%', height: '100%'}}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{mb: 3, alignItems: 'center'}}>
                <Typography variant="body2" sx={{color: 'text.secondary', mr: 1}}>
                    Filter by year:
                </Typography>
                <Chip
                    label="All"
                    size="small"
                    color={selectedYear === ALL_YEARS ? 'primary' : 'default'}
                    variant={selectedYear === ALL_YEARS ? 'filled' : 'outlined'}
                    onClick={() => setSelectedYear(ALL_YEARS)}
                />
                {availableYears.map((year) => (
                    <Chip
                        key={year}
                        label={year}
                        size="small"
                        color={selectedYear === year ? 'primary' : 'default'}
                        variant={selectedYear === year ? 'filled' : 'outlined'}
                        onClick={() => setSelectedYear(year)}
                    />
                ))}
            </Stack>

            {tagGroups.length > 0 && (
                <Box sx={{mb: 2}}>
                    <Typography variant="body2" sx={{color: 'text.secondary', mb: 1}}>
                        Filter by tag:
                    </Typography>
                    <Stack spacing={1}>
                        {tagGroups.map((category) => (
                            <Stack
                                key={category.label}
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{alignItems: 'center'}}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        color: category.color,
                                        minWidth: 150,
                                        flexShrink: 0
                                    }}
                                >
                                    {category.label}
                                </Typography>
                                {category.tags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        size="small"
                                        variant="outlined"
                                        clickable
                                        onClick={() => setSelectedTags((previous) => toggleItem(previous, tag))}
                                        sx={tagChipSx(tag, selectedTags.has(tag))}
                                    />
                                ))}
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            )}

            {hasFilter && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 3}}>
                    <Typography variant="caption" sx={{color: 'text.secondary'}}>
                        {visibleSofts.length} project{visibleSofts.length === 1 ? '' : 's'} shown
                    </Typography>
                    <Chip
                        label="Clear filters"
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            setSelectedYear(ALL_YEARS);
                            setSelectedTags(new Set());
                        }}
                    />
                </Stack>
            )}

            {visibleSofts.length === 0 ? (
                <Typography variant="body2" sx={{color: 'text.secondary', py: 4, textAlign: 'center'}}>
                    No project matches the selected filters.
                </Typography>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(3, minmax(0, 1fr))'
                        },
                        gap: 2,
                        alignItems: 'stretch'
                    }}
                >
                    {visibleSofts.map(renderSoftCard)}
                </Box>
            )}
        </Box>
    );
}
